"use client";

import { useHover } from "react-aria";
import { clsx } from "clsx";
import { pxToRem } from "@ui/lib/utils";

type RevealImageContainerProps = {
  children: React.ReactNode;
  position: "left" | "right";
  isActive: boolean;
  onHoverStart: () => void;
  className?: string;
  /**
   * Opacity of the mask gradient end (0 = transparent, 0.5 = semi-transparent)
   * @default 0
   */
  maskEndOpacity?: number;
};

export function RevealImageContainer({
  children,
  position,
  isActive,
  onHoverStart,
  className,
  maskEndOpacity = 0,
}: RevealImageContainerProps) {
  const { hoverProps } = useHover({ onHoverStart });

  const overlapWidth = (1 / 3) * 100;
  const gradientWidth = (1 / 6) * 100;

  const maskGradientDirection = position === "left" ? "to right" : "to left";
  const endColor = maskEndOpacity === 0 ? "transparent" : `rgba(255,255,255,${maskEndOpacity})`;
  const maskImage = `linear-gradient(${maskGradientDirection}, white calc(100% - ${overlapWidth / 2}% - ${gradientWidth / 2}%), ${endColor} calc(100% - ${overlapWidth / 2}%))`;

  const maskPosition =
    position === "left"
      ? isActive
        ? `calc(-100% - ${overlapWidth + gradientWidth}%) 0`
        : "-100% 0"
      : isActive
        ? `${overlapWidth + gradientWidth}% 0`
        : "0 0";

  return (
    <div
      className={clsx(
        "tw-relative tw-bg-white tw-overflow-hidden tw-rounded-t-lg tw-transition-all tw-duration-500",
        className,
      )}
      style={{
        maskImage,
        maskSize: "200% 100%",
        maskPosition,
        transform: isActive ? "translateY(0)" : `translateY(${pxToRem(12)})`,
        zIndex: isActive ? 1 : 0,
      }}
      {...hoverProps}
    >
      {children}
    </div>
  );
}
